/**
 * Les barres de la voix, lues sur l'analyseur d'une piste de micro (weekly du 03/11, W1).
 *
 * Le calcul est celui de `useAudioWaveform` (`@livekit/components-react`), que lisait l'ancienne vignette : la
 * moyenne des valeurs absolues de chaque bande, sa racine, fois `volMultiplier`. Ce crochet lisait l'analyseur à
 * chaque image (`requestAnimationFrame`) et divisait le dernier relevé par le nombre d'images écoulées depuis la
 * barre précédente, environ six à 60 images par seconde : `FRAMES_PER_UPDATE` garde ce gabarit, pour que les barres
 * aient la même allure.
 *
 * Ce qui change, c'est le rythme : un relevé toutes les `VOICE_LEVELS_EVERY_MS`, plus une boucle d'images par
 * vignette. Mesuré sur la box, ces boucles faisaient produire des images à la page de l'élève sans arrêt (le
 * « (program) » du profil), pour des barres qui ne changent que dix fois par seconde.
 */
import { createAudioAnalyser, type LocalAudioTrack, type RemoteAudioTrack } from "livekit-client"

export const VOICE_LEVELS_EVERY_MS = 100
const BAR_COUNT = 3
const VOL_MULTIPLIER = 5
const FRAMES_PER_UPDATE = 6
/** La plus petite fenêtre d'analyse (`getFFTSizeValue` de LiveKit pour trois barres). */
const FFT_SIZE = 32

/** Trois barres, de 0 à 1 ou un peu plus (la vue les borne), à partir d'un relevé de l'analyseur. */
export function voiceLevels(samples: ArrayLike<number>, barCount = BAR_COUNT): number[] {
    const blockSize = Math.floor(samples.length / barCount)
    const levels: number[] = []
    for (let bar = 0; bar < barCount; bar++) {
        let sum = 0
        for (let i = 0; i < blockSize; i++) sum += Math.abs(samples[bar * blockSize + i] / FRAMES_PER_UPDATE)
        levels.push(blockSize > 0 ? Math.sqrt(sum / blockSize) * VOL_MULTIPLIER : 0)
    }
    return levels
}

const SILENT: readonly number[] = [0, 0, 0]

/**
 * Relit les barres d'une piste de micro toutes les 100 ms et les pousse à `push`, sans passer par l'état de React :
 * au web, redessiner chaque vignette dix fois par seconde coûtait le fil principal de la page de l'élève (mesuré
 * sur la box). Rend de quoi arrêter : l'analyseur est fermé et les barres retombent (`[0, 0, 0]`). Sans flux audio,
 * rien n'est ouvert.
 */
export function listenVoiceLevels(
    track: LocalAudioTrack | RemoteAudioTrack,
    push: (levels: readonly number[]) => void
): () => void {
    if (track.mediaStream == null) return () => undefined
    const { analyser, cleanup } = createAudioAnalyser(track, { fftSize: FFT_SIZE })
    const samples = new Float32Array(FFT_SIZE)
    const timer = setInterval(() => {
        analyser.getFloatTimeDomainData(samples)
        push(voiceLevels(samples))
    }, VOICE_LEVELS_EVERY_MS)
    return () => {
        clearInterval(timer)
        void cleanup()
        push(SILENT)
    }
}
